'use client'

import React, { useState, useRef, useEffect } from 'react'
import { Collapsible, CollapsibleTrigger } from "@/components/ui/collapsible"
import { Button } from "@/components/ui/button"
import { ChevronDownIcon, ChevronUpIcon } from "lucide-react"
import { SingleProductEnum } from "@/constants/commerce-enum"
import SafeHtml from "@/hook/safeHtml";

interface CollapsibleContentProps {
    content?: any
    children?: string | React.ReactNode
    maxHeight?: number
    color?: string | boolean
}

export default function CollapsibleContentComponent({ content, children, maxHeight = 200, color = false }: CollapsibleContentProps) {
    const [isOpen, setIsOpen] = useState(false)
    const contentRef = useRef<HTMLDivElement>(null)
    const [contentHeight, setContentHeight] = useState(0)

    const updateContentHeight : any = () => contentRef.current && setContentHeight(contentRef.current.scrollHeight)

    useEffect(() => {
        updateContentHeight()
        window.addEventListener('resize', updateContentHeight)
        return () => window.removeEventListener('resize', updateContentHeight)
    }, [content])

    useEffect(() => updateContentHeight(), [isOpen])

    const shouldCollapse = contentHeight > maxHeight

    return (
        <Collapsible open={isOpen} onOpenChange={setIsOpen}>
            <div
                ref={contentRef}
                style={{
                    maxHeight: shouldCollapse && !isOpen ? `${maxHeight}px` : `${contentHeight}px`,
                    transition: 'max-height 0.3s ease-in-out',
                }}
                className="relative overflow-hidden"
            >
                {content ? (
                    <SafeHtml
                        html={content}
                        className="w-full text-justify leading-8 py-2 [&_div]:px-0 text-gray-800 [&_a]:text-orange-500 [&_p]:mt-2 [&_h1]:text-[16px] md:[&_h1]:text-[20px]  [&_h3]:text-[18px] md:[&_h3]:text-[20px] [&_h2]:text-[16px] md:[&_h2]:text-[20px] text-sm md:text-base [&_h3]:py-5 [&_h2]:py-5"
                    />
                ) : children}
                {shouldCollapse && !isOpen && (
                    <div className={`absolute bottom-0 left-0 right-0 h-16 ${color ? 'bg-gradient-to-t from-cream to-transparent' : "bg-gradient-to-t from-white to-transparent"} pointer-events-none`} />
                )}
            </div>
            {shouldCollapse && (
                <CollapsibleTrigger asChild>
                    <Button variant="none" className="border-none px-0 mt-2">
                        {isOpen ? (
                            <div className="flex flex-wrap text-xs text-orange-primary md:text-base items-center justify-between">
                                {SingleProductEnum.lessView}
                                <ChevronUpIcon className="w-4 h-4 md:w-6 md:h-6" />
                            </div>
                        ) : (
                            <div className="flex flex-wrap text-xs text-orange-primary md:text-base items-center justify-between">
                                {SingleProductEnum.moreView}
                                <ChevronDownIcon className="w-4 h-4 md:w-6 md:h-6" />
                            </div>
                        )}
                    </Button>
                </CollapsibleTrigger>
            )}
        </Collapsible>
    )
}